import axios from 'axios';

// Resolves the current sim build version and caches it in-memory.
//
// The reverse proxy publishes the running build's version (the contents of its
// `changeset` file) at the same-origin, no-cache endpoint `/sim-version`. The
// versioned-launch scheme sends users to `/sim/{version}/?case&org` (see
// getWebglLink.js). We resolve the version ahead of time and cache it so the
// Run-Scenario click can build the URL synchronously — an async fetch inside the
// click handler would get the popup (window.open) blocked by the browser.
//
// The version only changes on a sim deploy, so we refresh on a short interval
// (startSimVersionPolling) and, on any fetch failure, keep serving the last value
// we successfully resolved rather than breaking a launch.

// Build-time flag: only emit the versioned path in environments where the
// versioned build is actually deployed. Until an env sets
// VUE_APP_USE_VERSIONED_SIM_PATH=true, getWebglLink keeps the legacy unversioned
// `/sim` path so Run never targets a `/sim/{version}/` folder that doesn't exist yet.
export function isVersionedSimPathEnabled() {
  return process.env.VUE_APP_USE_VERSIONED_SIM_PATH === 'true';
}

const SIM_VERSION_URL = '/sim-version';
const REFRESH_INTERVAL_MS = 60 * 1000;

let cachedVersion = null;
let inFlight = null;

// Synchronous read of the last-resolved version (trimmed), or null if not yet resolved.
export function getCachedSimVersion() {
  return cachedVersion;
}

// Fetch `/sim-version` and update the cache. Concurrent calls share one request.
// Never rejects: on failure it resolves to the last-known value (possibly null).
export function refreshSimVersion() {
  if (inFlight) return inFlight;
  inFlight = axios
    .get(SIM_VERSION_URL, { responseType: 'text' })
    .then((res) => {
      const version = String(res.data).trim();
      if (version) cachedVersion = version;
      inFlight = null;
      return cachedVersion;
    })
    .catch(() => {
      inFlight = null;
      return cachedVersion;
    });
  return inFlight;
}

// Warm the cache now and refresh periodically. Call once at app init.
export function startSimVersionPolling() {
  refreshSimVersion();
  return setInterval(refreshSimVersion, REFRESH_INTERVAL_MS);
}
